import axios from 'axios';
import { useAuthStore } from '../store/authStore';

// In production this app is served by the same FastAPI server it talks to, so
// default to the page's own origin (works whether opened via localhost, a LAN
// IP, or a hostname) rather than hardcoding localhost — a hardcoded localhost
// default breaks API calls when another device on the LAN opens this app at
// e.g. http://192.168.x.x:8000, since "localhost" would resolve to THAT device,
// not the server. REACT_APP_API_URL still overrides this if explicitly set.
const API_URL = process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Automatically attach Bearer token to every request
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authService = {
  register: (username, email, password) =>
    api.post('/auth/register', { username, email, password }),

  login: (username, password) =>
    api.post('/auth/login', { username, password }),
};

// Investigations
export const investigationService = {
  list: () =>
    api.get('/investigations'),

  get: (id) =>
    api.get(`/investigations/${id}`),

  create: (data) =>
    api.post('/investigations', data),

  // Edits title/description/status/incident_date after creation (Aug 11 rework).
  // incident_date is investigator-set/editable freely, unlike created_at.
  update: (id, data) =>
    api.put(`/investigations/${id}`, data),

  delete: (id) =>
    api.delete(`/investigations/${id}`),

  getGraph: (id) =>
    api.get(`/investigations/${id}/graph`),

  fetchBlockchain: (id) =>
    api.post(`/investigations/${id}/fetch-blockchain`),

  importTransactions: (id, transactions) =>
    api.post(`/investigations/${id}/import`, { transactions }),

  getNotes: (id) =>
    api.get(`/investigations/${id}/notes`),

  addNote: (id, content, address = null) =>
    api.post(`/investigations/${id}/notes`, { content, address }),

  deleteNote: (investigationId, noteId) =>
    api.delete(`/investigations/${investigationId}/notes/${noteId}`),

  getTags: (id) =>
    api.get(`/investigations/${id}/tags`),

  addTag: (id, tagData) =>
    api.post(`/investigations/${id}/tags`, tagData),

  deleteTag: (investigationId, tagId) =>
    api.delete(`/investigations/${investigationId}/tags/${tagId}`),

  fetchByTxIds: (id, data) =>
    api.post(`/investigations/${id}/fetch-by-txids`, data),

  getTxLookupGraph: (id) =>
    api.get(`/investigations/${id}/tx-lookup-graph`),

  setGraphFromSeedTxs: (id) =>
    api.post(`/investigations/${id}/set-graph-from-seed-txs`),

  generateReport: (id, graphImage = null) =>
    api.post(`/investigations/${id}/report`, { graph_image: graphImage }, { responseType: 'blob' }),

  decodeDefi: (id) =>
    api.get(`/investigations/${id}/defi-decode`),

  getClusters: (id) =>
    api.get(`/investigations/${id}/clusters`),

  getGraphViewState: (id) =>
    api.get(`/investigations/${id}/graph-view-state`),

  saveGraphViewState: (id, hiddenEdges, hiddenNodes) =>
    api.post(`/investigations/${id}/graph-view-state`, {
      hidden_edges: Array.from(hiddenEdges),
      hidden_nodes: Array.from(hiddenNodes),
    }),

  // Collaborators / sharing (owner or admin manage; added June 20 2026)
  getCollaborators: (id) =>
    api.get(`/investigations/${id}/collaborators`),

  addCollaborator: (id, username, role = 'viewer') =>
    api.post(`/investigations/${id}/collaborators`, { username, role }),

  updateCollaborator: (id, userId, role) =>
    api.put(`/investigations/${id}/collaborators/${userId}`, { role }),

  removeCollaborator: (id, userId) =>
    api.delete(`/investigations/${id}/collaborators/${userId}`),
};

// Admin (user management — added June 20 2026, admin role required server-side)
export const adminService = {
  listUsers: () =>
    api.get('/admin/users'),

  createUser: (data) =>
    api.post('/admin/users', data),

  updateUser: (id, data) =>
    api.put(`/admin/users/${id}`, data),

  deleteUser: (id) =>
    api.delete(`/admin/users/${id}`),
};

// Admin -- Organizations (added July 2026, Superuser-only server-side). A Superuser
// oversees multiple client Organizations; each Organization has its own Admin(s)
// managing users/investigations fully isolated from every other Organization.
export const organizationService = {
  list: () =>
    api.get('/admin/organizations'),

  create: (data) =>
    api.post('/admin/organizations', data),

  update: (id, data) =>
    api.put(`/admin/organizations/${id}`, data),

  delete: (id) =>
    api.delete(`/admin/organizations/${id}`),
};

// Admin -- blockchain explorer API keys (added June 28 2026). GET returns masked
// status only (never the raw key); PUT applies immediately, no backend restart
// needed -- see App/api/modules/settings_store.py.
export const apiKeysService = {
  get: () =>
    api.get('/admin/api-keys'),

  update: (keys) =>
    api.put('/admin/api-keys', keys),

  // Deletes a key entirely (DB row + live env var) rather than overwriting it.
  delete: (key) =>
    api.delete(`/admin/api-keys/${key}`),
};

// Admin -- Access Requests (added July 2026, Superuser-only server-side). Public
// "request access" submissions from the marketing site's request-access.php form
// (unauthenticated intake, see App/api/routers/public.py) land here as 'pending'.
// Approving one creates the matching Organization (see organizationService above)
// and links back to it; rejecting just records the decision. See
// App/api/routers/admin.py's access-requests endpoints.
export const accessRequestService = {
  list: (status = null) =>
    api.get('/admin/access-requests', { params: status ? { status } : {} }),

  approve: (id, orgNameOverride = null) =>
    api.post(`/admin/access-requests/${id}/approve`,
      orgNameOverride ? { org_name_override: orgNameOverride } : {}),

  reject: (id, reviewNote = null) =>
    api.post(`/admin/access-requests/${id}/reject`,
      reviewNote ? { review_note: reviewNote } : {}),
};

// Demo trial accounts (added July 2026) — free 5-day trial, one per email ever.
// See App/api/routers/demo.py.
export const demoService = {
  signup: (username, email, password) =>
    api.post('/demo/signup', { username, email, password }),

  status: () =>
    api.get('/demo/status'),

  submitFeedback: (message) =>
    api.post('/demo/feedback', { message }),
};

// Admin -- Demo Accounts (added July 2026, Superuser-only server-side). Lets a
// Superuser see every trial's username/email for tracking, open (reactivate)
// or close (disable) one, and read submitted feedback. See
// App/api/routers/admin.py's demo-accounts / demo-feedback endpoints.
export const demoAdminService = {
  list: () =>
    api.get('/admin/demo-accounts'),

  close: (id) =>
    api.post(`/admin/demo-accounts/${id}/close`),

  open: (id) =>
    api.post(`/admin/demo-accounts/${id}/open`),

  // Permanently deletes the underlying User account (harsher than close, which
  // only disables login) -- the demo_accounts tracking row itself is kept
  // forever so the email can never sign up for a second free trial.
  remove: (id) =>
    api.delete(`/admin/demo-accounts/${id}`),

  listFeedback: () =>
    api.get('/admin/demo-feedback'),
};

// Transactions
export const transactionService = {
  getForAddress: (blockchain, walletAddress) =>
    api.get(`/transactions/${blockchain}/${walletAddress}`),

  getByHash: (blockchain, txHash) =>
    api.get(`/tx/${blockchain}/${txHash}`),
};

// Addresses
export const addressService = {
  get: (blockchain, address) =>
    api.get(`/addresses/${blockchain}/${address}`),

  getRisk: (blockchain, address) =>
    api.get(`/addresses/${blockchain}/${address}/risk`),

  setLabel: (blockchain, address, label) =>
    api.post(`/addresses/${blockchain}/${address}/label`, { label }),
};

// Known Address Registry
export const knownAddressService = {
  list: (blockchain = null, address_type = null) => {
    const params = {};
    if (blockchain)    params.blockchain    = blockchain;
    if (address_type)  params.address_type  = address_type;
    return api.get('/known-addresses', { params });
  },
  lookup: (address) =>
    api.get(`/known-addresses/lookup/${address}`),
  // 15s timeout: this call gates Graph.js's element-building effect (knownLoaded must flip
  // true before elements are ever built), so a request that stalls indefinitely -- rather than
  // failing outright -- silently leaves the whole graph canvas blank forever with no error
  // surfaced anywhere (Network tab shows it as pending, not failed). Graph.js's existing
  // .catch(() => setKnownMap({})).finally(() => setKnownLoaded(true)) already handles a
  // timeout the same as any other failure, so this alone is enough to let the graph render
  // (without known-address labels) instead of hanging forever. See CLAUDE.md, blank-canvas
  // hop-trace/wallet-trace investigation.
  bulkLookup: (addresses) =>
    api.get('/known-addresses/bulk-lookup', { params: { addresses: addresses.join(',') }, timeout: 15000 }),
  create: (data) =>
    api.post('/known-addresses', data),
  update: (id, data) =>
    api.put(`/known-addresses/${id}`, data),
  delete: (id) =>
    api.delete(`/known-addresses/${id}`),
};

// AI
export const aiService = {
  status: () =>
    api.get('/ai/status'),

  chat: (question) =>
    api.post('/ai/chat', { question }),

  analyzeAddress: (blockchain, address) =>
    api.get(`/ai/analyze/${blockchain}/${address}`),

  queryInvestigation: (id, question) =>
    api.post(`/ai/query/${id}`, { question }),

  generateReport: (id) =>
    api.get(`/ai/report/${id}`),
};

// Hop Tracer
export const hopTracerService = {
  run: (id, options = {}) =>
    api.post(`/investigations/${id}/hop-trace`, {
      max_hops:   options.maxHops   ?? 4,
      day_buffer: options.dayBuffer ?? 3,
      max_nodes:  options.maxNodes  ?? 100,
    }),

  getResult: (id) =>
    api.get(`/investigations/${id}/hop-trace-graph`),

  // V2
  startV2: (id, options = {}) =>
    api.post(`/investigations/${id}/hop-trace-v2`, {
      mode:              options.mode             ?? 'outbound',
      max_hops:          options.maxHops          ?? 4,
      day_buffer:        options.dayBuffer        ?? 3,
      max_nodes:         options.maxNodes         ?? 100,
      fan_out_threshold: options.fanOutThreshold  ?? 10,
      focus_address:     options.focusAddress     ?? null,
    }),

  getV2Status: (id) =>
    api.get(`/investigations/${id}/hop-trace-v2/status`),

  cancelV2: (id) =>
    api.post(`/investigations/${id}/hop-trace-v2/cancel`),

  getV2Result: (id) =>
    api.get(`/investigations/${id}/hop-trace-v2/result`),
};

export default api;

// Statements (separate database — wallet/bank statement uploads, parsed transactions)
export const statementService = {
  list: (wallet_address = null) =>
    api.get('/api/statements', { params: wallet_address ? { wallet_address } : {} }),

  get: (id) =>
    api.get(`/api/statements/${id}`),

  upload: (file, { walletAddress, blockchain, label, uploadedBy }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('wallet_address', walletAddress);
    if (blockchain)  formData.append('blockchain', blockchain);
    if (label)       formData.append('label', label);
    if (uploadedBy)  formData.append('uploaded_by', uploadedBy);
    // Posted with plain axios (not the shared `api` instance) so the browser
    // sets the multipart boundary itself — the shared instance forces a JSON
    // Content-Type header which would break the upload.
    return axios.post(`${API_URL}/api/statements/upload`, formData);
  },

  getTransactions: (id) =>
    api.get(`/api/statements/${id}/transactions`),

  delete: (id) =>
    api.delete(`/api/statements/${id}`),

  downloadUrl: (id) =>
    `${API_URL}/api/statements/${id}/download`,

  searchByAddress: (address) =>
    api.get('/api/statements/search/transactions', { params: { address } }),

  crossReference: (address) =>
    api.get(`/api/statements/cross-reference/${address}`),
};

// Investigation-scoped statement import (Aug 13 2026) -- DELIBERATELY SEPARATE
// from the global statementService above: stores uploads/transactions per-
// investigation in the main DB, and auto-correlates any row with a tx_hash
// against this investigation's graph (fetching it from the chain and merging
// it in if it's not already there). See App/api/routers/investigation_statements.py.
export const investigationStatementService = {
  list: (investigationId) =>
    api.get(`/investigations/${investigationId}/statements`),

  upload: (investigationId, file, label) => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    // Plain axios (not the shared `api` instance) so the browser sets the
    // multipart boundary itself -- same reasoning as statementService.upload above.
    return axios.post(`${API_URL}/investigations/${investigationId}/statements/upload`, formData, {
      headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
    });
  },

  getTransactions: (investigationId, uploadId) =>
    api.get(`/investigations/${investigationId}/statements/${uploadId}/transactions`),

  delete: (investigationId, uploadId) =>
    api.delete(`/investigations/${investigationId}/statements/${uploadId}`),
};

// Wallet Monitor (live address polling + SSE stream)
export const monitorService = {
  list: (id) =>
    api.get(`/investigations/${id}/monitors`),

  add: (id, address, label = '') =>
    api.post(`/investigations/${id}/monitors`, { address, label }),

  remove: (id, monitorId) =>
    api.delete(`/investigations/${id}/monitors/${monitorId}`),

  start: (id) =>
    api.post(`/investigations/${id}/monitors/start`),

  stop: (id) =>
    api.post(`/investigations/${id}/monitors/stop`),

  status: (id) =>
    api.get(`/investigations/${id}/monitors/status`),

  updateSettings: (id, enabled, maxDepth) =>
    api.post(`/investigations/${id}/monitors/settings`, { enabled, max_depth: maxDepth }),

  // Returns the SSE stream URL (used with native EventSource, not axios)
  streamUrl: (id) => `${API_URL}/investigations/${id}/monitors/stream`,

  // Returns the token for manual SSE auth header (EventSource can't set headers natively;
  // we include the token as a query param handled by the server)
  getToken: () => useAuthStore.getState().token,
};

export const auditService = {
  getLogs: (params = {}) =>
    api.get('/admin/audit-logs', { params }),

  // Superuser-only, platform-wide (see routers/admin.py::purge_audit_logs)
  purgeOlderThan: (olderThanDays) =>
    api.delete('/admin/audit-logs', { params: { older_than_days: olderThanDays } }),
};

// Client Incident Intake Form (Aug 18 2026) -- see App/api/routers/intake_forms.py
// and models.IntakeForm for the full lifecycle. Deliberately split into two halves:
// `public` (no auth, token is the bearer credential, used by IntakeFormPage.js) and
// investigator-facing methods below it (auth required, used by IntakeFormPanel.js).
export const intakeFormService = {
  public: {
    get: (token) =>
      axios.get(`${API_URL}/intake/${token}`),

    submit: (token, data) =>
      axios.post(`${API_URL}/intake/${token}/submit`, data),
  },

  get: (investigationId) =>
    api.get(`/investigations/${investigationId}/intake`),

  update: (investigationId, data) =>
    api.put(`/investigations/${investigationId}/intake`, data),

  getNotes: (investigationId) =>
    api.get(`/investigations/${investigationId}/intake/notes`),

  addNote: (investigationId, content) =>
    api.post(`/investigations/${investigationId}/intake/notes`, { content }),

  deleteNote: (investigationId, noteId) =>
    api.delete(`/investigations/${investigationId}/intake/notes/${noteId}`),
};
